import React, { useState } from 'react';
import { Link } from 'react-router';
import { useDispatch } from 'react-redux';

import { deleteNonGEASSOUser, getNonGEASSOUserAccounts, updateNonGEASSOUser } from '../../../../redux/actions';
import GenericOverlayDialog from '../../../dialogs/genericOverlayDialog';

const NonGEASSOUsers = () => {
	const [fields, setFields] = useState({
		sso: '',
		name: '',
		email: '',
		accountNumbers: ''
	});

	const [errors, setErrors] = useState({
		name: false,
		email: false,
		accountNumbers: false
	});
	const [isExistingUser, setIsExistingUser] = useState(false);
	const [showForm, setShowForm] = useState(false);
	const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
	const [message, setMessage] = useState('');

	const dispatch = useDispatch();

	const resetData = () => {
		setFields(fields => ({
			sso: fields.sso,
			name: '',
			email: '',
			accountNumbers: ''
		}));
		setErrors({
			name: false,
			email: false,
			accountNumbers: false
		});
		setIsExistingUser(false);
		setMessage('');
		setShowForm(false);
	};

	const isEmailIdValid = emailId => /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,5}$/.test(emailId);

	const isFieldValid = (fieldName, fieldValue) => {
		if (['accountNumbers'].includes(fieldName)) {
			if (fieldValue.trim() === '') {
				return false;
			}
			if (fieldName === 'accountNumbers' && !fieldValue.split(/\D/g).filter(accountNumber => accountNumber.trim()).length) {
				return false;
			}
		} else if (fieldName === 'email') {
			if (fieldValue.trim() === '') {
				return true;
			}
			if (fieldValue.trim() && !isEmailIdValid(fieldValue)) {
				return false;
			}
		}

		return true;
	};

	const handleBlur = e => {
		const { name: fieldName, value: fieldValue } = e.target;
		const isValid = isFieldValid(fieldName, fieldValue);

		if (!errors[fieldName] && !isValid) {
			setErrors({
				...errors,
				[fieldName]: true
			});
		}
	};

	const handleSearch = e => {
		e.preventDefault();
		resetData();

		dispatch(
			getNonGEASSOUserAccounts(`requestType=nonGeaSsoAccount&sso=${fields.sso}`, nonGEASSOUserAccountsResponse => {
				const userDetails = nonGEASSOUserAccountsResponse.responseObject;

				if (userDetails) {
					setFields(fields => ({
						...fields,
						name: userDetails.customerName || '',
						email: !userDetails.email || userDetails.email === 'null' ? '' : userDetails.email,
						accountNumbers: (userDetails.accountNumbers && userDetails.accountNumbers.length ? userDetails.accountNumbers : []).join('\n')
					}));
					setIsExistingUser(true);
				}
				setShowForm(true);
			})
		);
	};

	const handleChange = e => {
		const { name: fieldName, value: fieldValue } = e.target;

		if (fieldName === 'sso') {
			setFields({
				sso: fieldValue.trim(),
				name: '',
				email: '',
				accountNumbers: ''
			});
			if (errors.name || errors.email || errors.accountNumbers) {
				setErrors({
					name: false,
					email: false,
					accountNumbers: false
				});
			}
			showForm && setShowForm(false);
		} else {
			setFields(fields => ({
				...fields,
				[fieldName]: ['accountNumbers', 'name'].includes(fieldName) ? fieldValue : fieldValue.trim()
			}));

			const isValid = isFieldValid(fieldName, fieldValue);

			if (errors[fieldName] && isValid) {
				setErrors({
					...errors,
					[fieldName]: false
				});
			}
		}
		message && setMessage('');
	};

	const handleSave = () => {
		const payload = {
			ssoId: fields.sso.trim(),
			customerName: fields.name.trim(),
			email: fields.email.trim(),
			accountNumbers: fields.accountNumbers.split(/\D/g).filter(accountNumber => accountNumber.trim())
		};

		if (!payload.ssoId) {
			return;
		}
		if (payload.email && errors.email) {
			return;
		}
		if (!payload.accountNumbers.length) {
			setErrors(errors => ({ ...errors, accountNumbers: true }));
			return;
		}

		dispatch(
			updateNonGEASSOUser(payload, `requestType=nonGeaSsoAccount&sso=${fields.sso}`, nonGEASSOUserAccountsResponse => {
				const userDetails = nonGEASSOUserAccountsResponse.responseObject;

				if (userDetails) {
					setFields(fields => ({
						...fields,
						name: userDetails.customerName || '',
						email: userDetails.email || '',
						accountNumbers: (userDetails.accountNumbers && userDetails.accountNumbers.length ? userDetails.accountNumbers : []).join('\n')
					}));
					setIsExistingUser(true);
					setMessage('Updates have been saved.');
				}
			})
		);
	};

	const handleDeletion = () => {
		setShowDeleteConfirmationDialog(false);
		dispatch(
			deleteNonGEASSOUser(null, `requestType=nonGeaSsoAccount&sso=${fields.sso}`, nonGEASSOUserAccountsResponse => {
				if (nonGEASSOUserAccountsResponse.status) {
					resetData();
				}
			})
		);
	};

	return (
		<div className="nonGEASSOUser">
			<div className="push">
				<h1 className="pageTitle">Manage Non GEA SSO Users</h1>
			</div>
			<form className="nonGEASSOUserSearchForm push" onSubmit={handleSearch}>
				<p className="nonGEASSOUserSearchFormRow col-12 col-sm-8 col-md-6 col-lg-4">
					<input type="text" name="sso" placeholder="SSO" value={fields.sso} onChange={handleChange} />
					<button disabled={!fields.sso.trim()}>Search</button>
				</p>
			</form>
			{showForm && (
				<form className="nonGEASSOUserForm col-12 col-sm-8 col-md-6 col-lg-5 push">
					{message && <p className="importantText bold mt-0">{message}</p>}
					<div className="nonGEASSOUserFormRow">
						<b>SSO:</b>
						<span className="fullWidth">{fields.sso}</span>
					</div>
					<div className="nonGEASSOUserFormRow">
						<label htmlFor="name">
							<b>Name:</b>
						</label>
						<div>
							<input id="name" name="name" type="text" className="fullWidth" value={fields.name} onChange={handleChange} onBlur={handleBlur} />
						</div>
					</div>
					<div className={`nonGEASSOUserFormRow${errors.email ? ' error' : ''}`}>
						<label htmlFor="email">
							<b>Email:</b>
						</label>
						<div>
							<input id="email" name="email" type="text" className="fullWidth" value={fields.email} onChange={handleChange} onBlur={handleBlur} />
							{errors.email && <p className="message">Please enter a valid email</p>}
						</div>
					</div>
					<div className={`nonGEASSOUserFormRow${errors.accountNumbers ? ' error' : ''}`}>
						<label htmlFor="account-numbers">
							<b>
								Account Numbers<span className="required">*</span>:
							</b>
						</label>
						<div>
							<textarea
								id="account-numbers"
								name="accountNumbers"
								className="fullWidth"
								rows={10}
								value={fields.accountNumbers}
								onChange={handleChange}
								onBlur={handleBlur}
							/>
							{errors.accountNumbers && <p className="message">Please enter valid account numbers</p>}
						</div>
					</div>
					<p className="flex justify-between">
						<button type="button" className="orangeButton" onClick={setShowDeleteConfirmationDialog.bind(this, true)} disabled={!isExistingUser}>
							Delete User
						</button>
						<button type="button" onClick={handleSave}>
							Save
						</button>
					</p>
				</form>
			)}
			<p>
				<Link to="/siteManagement">Back to Site Management</Link>
			</p>
			{showDeleteConfirmationDialog && (
				<GenericOverlayDialog
					buttons={['no', 'yes']}
					genericDialogHeading={`Confirm`}
					genericDialogText={`Are you sure you want to delete the user?`}
					flag="DeleteConfirmationDialog"
					handleYes={handleDeletion}
					handleDialog={setShowDeleteConfirmationDialog.bind(this, false)}
					showDialog={true}
					defaultTop
				/>
			)}
		</div>
	);
};

export default NonGEASSOUsers;



// WEBPACK FOOTER //
// ./src/App/components/PostLogin/SiteManagement/NonGEASSOUsers/index.jsx